import { useCallback, useEffect, useRef, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { useDispatch, useSelector } from 'react-redux';
import { fitToScreenshareGrid } from 'features/screenshare/utils/fitToScreenshareGrid';
import { SourceDetails } from 'features/layout/types';
import { gridDimensionsChanged, selectGridDimensions } from 'features/screenshare/screenshareSlice';

export const useScreenshareGrid = (screenshares: SourceDetails[]) => {
  const dispatch = useDispatch();
  const dimensions = useSelector(selectGridDimensions);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const sizeRef = useRef({ width: 0, height: 0 });

  const calculateDimensions = useCallback(
    (width: number, height: number) => {
      if (width === 0 || height === 0) {
        return;
      }

      const calculatedDimensions = fitToScreenshareGrid(width, height, screenshares.length);

      dispatch(
        gridDimensionsChanged({
          cols: calculatedDimensions.cols,
          rows: calculatedDimensions.rows,
          tileHeight: calculatedDimensions.tileHeight,
          tileWidth: calculatedDimensions.tileWidth,
          maxTiles: calculatedDimensions.maxTiles,
        })
      );
    },
    [dispatch, screenshares.length]
  );

  const { ref: containerResizeRef } = useResizeDetector({
    refreshMode: 'debounce',
    refreshRate: 16,
    onResize: ({ width, height }) => {
      if (width && height) {
        sizeRef.current = { width, height };
        setSize({ width, height });
        calculateDimensions(width, height);
      }
    },
  });

  useEffect(() => {
    calculateDimensions(sizeRef.current.width, sizeRef.current.height);
  }, [calculateDimensions]);

  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerResizeRef.current = node;
    },
    [containerResizeRef]
  );

  return {
    containerRef,
    dimensions,
    size,
  };
};
